import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
const TICKS = 60;
const STRONG_TICK = Math.round(70 / 100 * TICKS);
interface LifeScoreRingProps {
  score: number;
  size?: number;
  tone?: 'dark' | 'light';
  bandLabel: string;
  caption?: string;
}

/**
 * Beelyv's proprietary score dial: 60 discrete instrument ticks rather than a
 * continuous activity ring. Ticks past the 70-point threshold turn gold, so
 * reaching the healthy range is a visible achievement.
 */
export function LifeScoreRing({
  score,
  size = 208,
  tone = 'dark',
  bandLabel,
  caption
}: LifeScoreRingProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const filled = Math.round(clamped / 100 * TICKS);
  const empty = tone === 'dark' ? 'rgba(245,241,230,0.16)' : 'var(--bee-line)';
  const base = tone === 'dark' ? 'rgba(245,241,230,0.9)' : 'var(--bee-soft)';
  return <div className="relative shrink-0" style={{
    width: size,
    height: size
  }} role="img" aria-label={`Life Score ${clamped} / 100 — ${bandLabel}`} data-id="element-840" data-mp="3vtiioa2l8vud">
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" data-id="element-841" data-mp="lall6dpmr3ar">
        {Array.from({
        length: TICKS
      }).map((_, i) => {
        const angle = i * 360 / TICKS - 90;
        const rad = angle * Math.PI / 180;
        const isFilled = i < filled;
        const color = isFilled ? i >= STRONG_TICK ? 'var(--bee-gold)' : base : empty;
        const inner = isFilled ? 36 : 38;
        return <line key={i} x1={50 + inner * Math.cos(rad)} y1={50 + inner * Math.sin(rad)} x2={50 + 46 * Math.cos(rad)} y2={50 + 46 * Math.sin(rad)} stroke={color} strokeWidth={i % 5 === 0 ? 2.2 : 1.6} strokeLinecap="round" data-id="element-842" data-mp="190fysxhrwvw2" data-mpi={i} />;
      })}
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-5 text-center" data-id="element-843" data-mp="3ksml7l2i712u">
        <div className="flex items-baseline gap-1" data-id="element-844" data-mp="3crne5wsfsi5h">
          <AnimatePresence mode="popLayout" initial={false} data-id="element-845" data-mp="38paarf15wwbv">
            <motion.span key={clamped} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className={`bee-num text-[52px] font-bold leading-none ${tone === 'dark' ? 'text-sand' : 'text-deep'}`} data-id="element-846" data-mp="38r3gms3ybcit">
              {clamped}
            </motion.span>
          </AnimatePresence>
          <span className={`bee-num text-[13px] font-medium ${tone === 'dark' ? 'text-ondark' : 'text-muted'}`} data-id="element-847" data-mp="99dk354yiprg">
            /100
          </span>
        </div>
        <span className={`bee-overline text-[9px] font-bold ${clamped >= 70 ? 'text-gold' : tone === 'dark' ? 'text-ondark' : 'text-muted'}`} data-id="element-848" data-mp="j68jrrcyuw7b">
          {bandLabel}
        </span>
        {caption && <p className={`mt-1 text-[11px] leading-snug ${tone === 'dark' ? 'text-ondark' : 'text-muted'}`} data-id="element-849" data-mp="11uanjyunx4ii">
            {caption}
          </p>}
      </div>
    </div>;
}